import { supabase } from '@/integrations/supabase/client';

/**
 * A candidate event being checked against the user's existing imported events
 * before it gets inserted. Comes from Claude Vision extraction output or 
 * manual typed entry.
 */
export interface CandidateEvent {
  title: string;
  event_date: string;     // YYYY-MM-DD format
  start_time: string | null; // H:MM AM/PM format or null
  end_time?: string | null;
  is_all_day?: boolean;
  location?: string | null;
  notes?: string | null;
  child?: string | null;
  source_type: 'schedule' | 'newsletter' | 'work_calendar' | 'manual';
  is_recurring?: boolean;
}

/**
 * A match returned from the database dedup function. Contains the existing 
 * event that conflicts with the candidate, plus a match_type telling the 
 * UI how to handle it.
 */
export interface DuplicateMatch {
  id: string;
  title: string;
  event_date: string;
  start_time: string | null;
  source_type: string;
  match_type: 'exact' | 'possible' | 'overlap';
}

/**
 * The result of running dedup on a single candidate event.
 */
export interface DedupResult {
  candidate: CandidateEvent;
  action: 'skip' | 'needs_review' | 'import_with_note' | 'import_clean';
  matches: DuplicateMatch[];
}

/**
 * Converts a 12-hour time string like "3:30 PM" to a Postgres time format
 * like "15:30:00". Returns null if input is null or unparseable.
 */
function convertTo24Hour(time12h: string | null | undefined): string | null {
  if (!time12h) return null;
  
  const trimmed = time12h.trim();
  const match = trimmed.match(/^(\d{1,2}):?(\d{0,2})\s*(AM|PM|am|pm)?$/i);
  if (!match) return null;
  
  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const period = match[3]?.toUpperCase();
  
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  
  const hh = hours.toString().padStart(2, '0');
  const mm = minutes.toString().padStart(2, '0');
  return `${hh}:${mm}:00`;
}

/**
 * Checks a single candidate event against the user's existing imported_events
 * for potential duplicates. Returns a DedupResult with guidance on how to 
 * proceed:
 * 
 *   - 'skip'              → Do not import. Exact match already exists.
 *   - 'needs_review'      → Show user a warning in preview UI, let them decide.
 *   - 'import_with_note'  → Import anyway, but show informational note about
 *                           another event at the same time.
 *   - 'import_clean'      → Safe to import, no conflicts.
 */
export async function checkEventDuplicate(
  userId: string,
  candidate: CandidateEvent
): Promise<DedupResult> {
  const startTime24h = convertTo24Hour(candidate.start_time);
  
  const { data, error } = await (supabase.rpc as any)('find_event_duplicates', {
    p_user_id: userId,
    p_title: candidate.title,
    p_event_date: candidate.event_date,
    p_start_time: startTime24h,
  });
  
  if (error) {
    console.error('[eventDedup] find_event_duplicates RPC failed:', error);
    // Fail open: if dedup check errors out, let the import proceed.
    // Better to have a duplicate than to silently lose data.
    return {
      candidate,
      action: 'import_clean',
      matches: [],
    };
  }
  
  const matches = (data ?? []) as DuplicateMatch[];
  
  if (matches.length === 0) {
    return { candidate, action: 'import_clean', matches: [] };
  }
  
  // Priority: exact > possible > overlap
  const hasExact = matches.some(m => m.match_type === 'exact');
  const hasPossible = matches.some(m => m.match_type === 'possible');
  
  if (hasExact) {
    return { candidate, action: 'skip', matches };
  }
  if (hasPossible) {
    return { candidate, action: 'needs_review', matches };
  }
  return { candidate, action: 'import_with_note', matches };
}

/**
 * A match row returned from the batch RPC. Same shape as DuplicateMatch, plus
 * the index of the candidate (in the original input array) it belongs to.
 */
interface BatchMatchRow extends DuplicateMatch {
  candidate_index: number;
}

/**
 * Resolves a candidate's matches into a DedupResult action.
 * Note: the single-event path currently has inline logic; keep it in sync.
 * Priority: exact > possible > overlap.
 */
function resolveDedupAction(
  candidate: CandidateEvent,
  matches: DuplicateMatch[]
): DedupResult {
  if (matches.length === 0) {
    return { candidate, action: 'import_clean', matches: [] };
  }
  const hasExact = matches.some(m => m.match_type === 'exact');
  const hasPossible = matches.some(m => m.match_type === 'possible');
  if (hasExact) return { candidate, action: 'skip', matches };
  if (hasPossible) return { candidate, action: 'needs_review', matches };
  return { candidate, action: 'import_with_note', matches };
}

/**
 * Batch version: checks many candidate events at once in a SINGLE round-trip
 * via the `find_event_duplicates_batch` RPC. Used by the import preview screens
 * that extract multiple events from a single photo upload.
 *
 * Returns an array of DedupResults in the same order as the input candidates.
 *
 * Fails CLOSED (issue #37): if the RPC errors, this THROWS rather than
 * returning "no duplicates." The caller (SchedulePreview.handleSaveToKin) has
 * an existing try/catch that surfaces an error toast and lets the user retry,
 * so a dropped request on flaky cellular can never silently import duplicates.
 */
export async function checkEventDuplicatesBatch(
  userId: string,
  candidates: CandidateEvent[]
): Promise<DedupResult[]> {
  if (candidates.length === 0) return [];

  // Convert times up front so the DB receives Postgres-ready 24h strings,
  // exactly as the single-event path does before calling the RPC.
  const payload = candidates.map(c => ({
    title: c.title,
    event_date: c.event_date,
    start_time: /^\d{1,2}$/.test((c.start_time ?? '').trim()) ? null : convertTo24Hour(c.start_time),
  }));

  const { data, error } = await (supabase.rpc as any)('find_event_duplicates_batch', {
    p_user_id: userId,
    p_candidates: payload,
  });

  if (error) {
    console.error('[eventDedup] find_event_duplicates_batch RPC failed:', error);
    // Fail CLOSED: never treat a failed dedup check as "no duplicates."
    throw new Error('Duplicate check failed. Please try again.');
  }

  // Group the flat match rows back by candidate index.
  const rows = (data ?? []) as BatchMatchRow[];
  const matchesByIndex = new Map<number, DuplicateMatch[]>();
  for (const row of rows) {
    const list = matchesByIndex.get(row.candidate_index) ?? [];
    list.push({
      id: row.id,
      title: row.title,
      event_date: row.event_date,
      start_time: row.start_time,
      source_type: row.source_type,
      match_type: row.match_type,
    });
    matchesByIndex.set(row.candidate_index, list);
  }

  return candidates.map((candidate, i) =>
    resolveDedupAction(candidate, matchesByIndex.get(i) ?? [])
  );
}

/**
 * Splits a batch of DedupResults into three buckets for UI rendering:
 *   - autoImport: Events safe to import silently
 *   - needsReview: Events the user must decide on (possible duplicates)
 *   - autoSkipped: Events silently skipped as exact duplicates
 * 
 * Used by import preview screens to organize the event list cleanly.
 */
export function partitionDedupResults(results: DedupResult[]): {
  autoImport: DedupResult[];
  needsReview: DedupResult[];
  autoSkipped: DedupResult[];
} {
  const autoImport: DedupResult[] = [];
  const needsReview: DedupResult[] = [];
  const autoSkipped: DedupResult[] = [];
  
  for (const result of results) {
    if (result.action === 'skip') {
      autoSkipped.push(result);
    } else if (result.action === 'needs_review') {
      needsReview.push(result);
    } else {
      autoImport.push(result);
    }
  }
  
  return { autoImport, needsReview, autoSkipped };
}

/**
 * Helper: generates a human-readable summary of dedup results for display
 * in the preview screen header.
 * 
 * Examples:
 *   "18 events ready to import"
 *   "15 events ready, 2 need your review, 1 duplicate skipped"
 *   "All 3 events are already in your Kin"
 */
export function summarizeDedupResults(results: DedupResult[]): string {
  const { autoImport, needsReview, autoSkipped } = partitionDedupResults(results);
  
  const parts: string[] = [];
  
  if (autoImport.length > 0) {
    parts.push(
      `${autoImport.length} event${autoImport.length === 1 ? '' : 's'} ready`
    );
  }
  
  if (needsReview.length > 0) {
    parts.push(
      `${needsReview.length} need${needsReview.length === 1 ? 's' : ''} your review`
    );
  }
  
  if (autoSkipped.length > 0) {
    parts.push(
      `${autoSkipped.length} duplicate${autoSkipped.length === 1 ? '' : 's'} skipped`
    );
  }
  
  if (parts.length === 0) {
    return 'No events to import';
  }
  
  if (autoImport.length === 0 && needsReview.length === 0 && autoSkipped.length > 0) {
    return `All ${autoSkipped.length} event${autoSkipped.length === 1 ? ' is' : 's are'} already in your Kin`;
  }
  
  return parts.join(', ');
}
